<template>
  <Modal
    :title="title"
    :saving="saving"
    :disabled="formDisabled"
    @submit="handleSubmit"
    @hide-modal="$emit('hide-modal')"
  >
    <div class="section-spacing-20">
      <div>
        <WarningMessage v-for="warning in changes.warnings" :key="warning">
          {{ warning }}
        </WarningMessage>
        <Card headless>
          <div class="d-flex flex-column">
            <div class="d-flex align-items-center justify-content-between px-4">
              <div class="d-flex align-items-center">
                <div class="whitespace-nowrap mr-2"><b>Invoice Date:</b></div>
                <Datepicker
                  v-model="invoiceDate"
                  label-class="date-input"
                  name="Invoice Date"
                  no-label
                  :field-error-message="v$.invoiceDate.$errors[0]?.$message"
                  data-cy="invoice-date-input"
                />
              </div>
              <Divider vertical></Divider>
              <div v-if="!invoice.job_id">
                <b>Invoice Date:</b>
                {{
                  invoice.invoice_date
                    ? formatDateWithTimezone(invoice.invoice_date)
                    : "--"
                }}
              </div>
              <div v-else>
                <b>Work Completed Date:</b>
                {{
                  getProperty(invoice, "signature.signature_time")
                    ? formatDateWithTimezone(invoice.signature.signature_time)
                    : "--"
                }}
              </div>
              <Divider vertical></Divider>
              <div>
                <b>Batched Date:</b>
                {{
                  invoice.batched_date
                    ? formatDateWithTimezone(invoice.batched_date)
                    : "--"
                }}
              </div>
            </div>
            <div v-if="changes.errors && changes.errors.length">
              <div
                v-for="error in changes.errors"
                :key="error"
                class="text-danger"
              >
                <i class="fa-solid fa-circle-exclamation mr-1"></i>
                {{ error }}
              </div>
            </div>
          </div>
        </Card>
      </div>
      <TextArea
        v-if="formIsDirty"
        v-model="reason"
        label="*Reason for Editing the Invoice Date:"
        name="Reason"
        :error-message="firstErrorMessage(v$.reason)"
      ></TextArea>
      <div v-if="changes.messages && changes.messages.length" class="summary">
        <div class="font-bold">Saving these changes will:</div>
        <div v-for="(change, i) in changes.messages" :key="`${i}-${change}`">
          • {{ change }}
        </div>
      </div>
    </div>
  </Modal>
</template>

<script>
import WarningMessage from "serviceshift-ui/components/General/WarningMessage.vue";
import { mapActions } from "vuex";
import { EventBus } from "@/lib/event_bus.js";
import { firstErrorMessage } from "@/lib/vuelidate_helpers";

import Card from "@/components/Card.vue";
import Datepicker from "@/components/Datepicker.vue";
import Divider from "@/components/Divider.vue";
import Modal from "@/components/Modal.vue";
import TextArea from "@/components/TextArea.vue";
import { formatDateWithTimezone, getProperty } from "@//lib/helpers.js";
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";

export default {
  components: {
    Card,
    Datepicker,
    Divider,
    Modal,
    TextArea,
    WarningMessage
  },
  props: {
    /*
      invoice: {
        invoice_id: number;
        kind: string;
        invoice_date: string;
        signature: {
          signature_time: string;
        };
        batched_date: string;
        cancelled: boolean;
      };
    */
    invoice: {
      type: Object,
      default: null
    }
  },
  emits: ["hide-modal"],
  setup() {
    return {
      v$: useVuelidate()
    };
  },
  data() {
    return {
      invoiceDate: this.invoice.invoice_date,
      reason: "",
      /*
        changes: {
          messages: String[];
          warnings: String[];
          errors?: String[];
        }
      */
      changes: {},
      saving: false
    };
  },
  computed: {
    title() {
      if (!this.invoice) return "Edit Date for Invoice";
      const { invoice_id, cancelled, kind, job_id } = this.invoice;
      let invoiceType = "";
      if (!job_id) {
        invoiceType = " (Jobless)";
      }
      if (cancelled) {
        invoiceType = " (Cancelled)";
      }
      if (kind === "credit_memo") {
        invoiceType = " (Credit Memo)";
      }
      return `Edit Date for Invoice #${invoice_id || "--"}${invoiceType}`;
    },
    formIsDirty() {
      return this.invoiceDate !== this.invoice.invoice_date;
    },
    formDisabled() {
      // Disable form if no changes have been made or there are api errors
      return (
        !this.formIsDirty ||
        (this.changes.errors && this.changes.errors.length > 0)
      );
    }
  },
  watch: {
    invoiceDate(newValue, oldValue) {
      if (newValue !== oldValue && newValue) {
        this.fetchChanges();
      }
    }
  },
  validations() {
    return {
      invoiceDate: { required, $autoDirty: true },
      reason: { required, $autoDirty: true }
    };
  },
  methods: {
    firstErrorMessage,
    ...mapActions(["fetchInvoiceDateChangeSummary", "updateInvoiceDate"]),
    formatDateWithTimezone,
    getProperty,
    async handleSubmit() {
      const valid = await this.v$.$validate();
      if (valid) {
        this.saving = true;
        try {
          await this.updateInvoiceDate({
            link: this.invoice.links.edit_invoice_date,
            data: {
              invoice_date: this.invoiceDate,
              reason: this.reason
            }
          });
          window.notify.success("Invoice date updated successfully!");
          this.$emit("hide-modal");
          EventBus.$emit("invoice-date-updated");
        } catch (e) {
          // do nothing
        }
        this.saving = false;
      }
    },
    async fetchChanges() {
      if (this.invoice.links.validate_invoice_date_change) {
        this.changes = await this.fetchInvoiceDateChangeSummary({
          link: this.invoice.links.validate_invoice_date_change,
          params: {
            invoice_date: this.invoiceDate,
            reason: this.reason
          }
        });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
::v-deep .date-input {
  max-width: 130px;
}

.summary {
  background: var(--light-gray);
  border: 1px solid var(--gray);
  border-radius: 5px;
  padding: 10px;
}
</style>
